import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Dots } from '@zendeskgarden/react-loaders';
import TableRowErrorBoundary from './TableRowErrorBoundary';

// Helper function to safely render any value
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    if (value.label) return String(value.label);
    if (value.name) return String(value.name);
    if (value.title) return String(value.title);
    return '[Object]';
  }
  return String(value);
};

// Main container following Cases structure exactly
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #ffffff;
  width: 100%;
`;

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Title = styled.h1`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 32px;
  line-height: 40px;
  color: #2f3941;
  letter-spacing: -0.8px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #68737d;
  letter-spacing: -0.154px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SearchFilter = styled.div`
  background: #ffffff;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const SearchInput = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  width: 400px;
  height: 40px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #c2c8cc;
  letter-spacing: -0.154px;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
    color: #2f3941;
  }
  
  &::placeholder {
    color: #c2c8cc;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const FilterButton = styled.button`
  background: #ffffff;
  border: 1px solid #8d4f1b;
  border-radius: 4px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  letter-spacing: -0.154px;
  
  &:hover {
    background: rgba(141, 79, 27, 0.08);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
  min-width: 800px;
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
  
  @media (max-width: 480px) {
    min-width: 500px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  flex: 1;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 0;
    margin: 0 -16px;
    padding: 0 16px;
  }
  
  @media (max-width: 480px) {
    margin: 0 -12px;
    padding: 0 12px;
    display: none; /* Hide table on mobile */
  }
`;

const MobileCardContainer = styled.div`
  display: none;
  
  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    
    /* Hide scrollbars */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
`;

const MobileCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #d8dcde;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const MobileCardTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 18px;
  color: #2f3941;
  letter-spacing: -0.154px;
  flex: 1;
`;

const MobileCardDate = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.154px;
`;

const MobileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const MobileCardLabel = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.154px;
  min-width: 80px;
`;

const MobileCardValue = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  letter-spacing: -0.154px;
  text-align: right;
  flex: 1;
`;

const MobileCardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #e9ebed;
  margin-top: 4px;
`;

const TableHeader = styled.thead`
  background: #ffffff;
`;

const TableHeaderCell = styled.th`
  background: #ffffff;
  padding: 26px 12px;
  text-align: left;
  border-bottom: 1px solid #d8dcde;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    padding: 20px 8px;
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 6px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const TableHeaderRow = styled.tr`
  background: #ffffff;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  background: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:active {
    background: #e9ecef;
  }
  
  @media (max-width: 768px) {
    &:hover {
      background: #f5f5f5;
    }
    
    &:active {
      background: #e9ecef;
    }
  }
  
  /* Add better touch targets for mobile */
  @media (max-width: 480px) {
    &:first-child td {
      border-top: 1px solid #e9ebed;
    }
  }
`;

const TableCell = styled.td`
  background: #ffffff;
  padding: 20px 12px;
  border-bottom: 1px solid #e9ebed;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    padding: 16px 8px;
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 6px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const ConfidenceText = styled.span`
  color: #87929d;
  
  @media (max-width: 480px) {
    display: block;
    margin-top: 2px;
  }
`;

const StatusTag = styled.div<{ status: 'completed' | 'pending' | 'reviewing' }>`
  background: ${props => props.status === 'completed' ? '#058541' : props.status === 'reviewing' ? '#17a2b8' : '#ffb057'};
  color: ${props => props.status === 'completed' ? '#ffffff' : props.status === 'reviewing' ? '#ffffff' : '#703815'};
  padding: 2px 8px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.000427656px;
  
  @media (max-width: 480px) {
    padding: 1px 6px;
    font-size: 11px;
    line-height: 14px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  text-decoration: underline;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: color 0.2s ease;
  padding: 8px 4px;
  margin: -8px -4px;
  
  &:hover {
    color: #2f3941;
  }
  
  &:active {
    color: #8d4f1b;
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
    padding: 10px 6px;
    margin: -10px -6px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
    padding: 12px 8px;
    margin: -12px -8px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const PageButton = styled.button<{ active?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background: ${props => props.active ? 'rgba(141, 79, 27, 0.08)' : 'transparent'};
  color: ${props => props.active ? '#2f3941' : '#68737d'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'SF Pro', sans-serif;
  font-weight: ${props => props.active ? '590' : '400'};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'rgba(141, 79, 27, 0.08)' : 'rgba(0, 0, 0, 0.04)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

export interface Column {
  key: string;
  title: string;
  render?: (value: any, record: any) => React.ReactNode;
}

export interface FilterableTableProps {
  title: string;
  description?: string;
  columns: Column[];
  data: any[];
  loading?: boolean;
  searchPlaceholder?: string;
  searchFields?: string[];
  maxRows?: number;
  itemsPerPage?: number;
  showPagination?: boolean;
  onRowClick?: (record: any) => void;
  headerActions?: React.ReactNode;
  mobileCardConfig?: {
    titleKey: string;
    dateKey?: string;
    rows: Array<{
      label: string;
      key: string;
    }>;
  };
}

const FilterableTable: React.FC<FilterableTableProps> = ({
  title,
  description,
  columns,
  data,
  loading = false,
  searchPlaceholder = "Search cases...",
  searchFields = [],
  maxRows,
  itemsPerPage = 10,
  showPagination = true,
  onRowClick,
  headerActions,
  mobileCardConfig
}) => {
  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setFilteredData(data);
    setCurrentPage(1); // Reset to first page when data changes
  }, [data]);

  const handleFilter = () => {
    if (!filterText.trim()) {
      setFilteredData(data);
      setCurrentPage(1);
      return;
    }

    const searchTerm = filterText.toLowerCase();
    
    const filtered = data.filter(item => {
      // If specific search fields are provided, search only in those fields
      if (searchFields.length > 0) {
        return searchFields.some(field => {
          const value = item[field];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      }
      
      // Otherwise, search in all string fields
      return Object.values(item).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm)
      );
    });
    
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page after filtering
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFilter();
    }
  };

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = showPagination ? Math.ceil(totalItems / itemsPerPage) : 1;
  const startIndex = showPagination ? (currentPage - 1) * itemsPerPage : 0;
  const endIndex = showPagination ? Math.min(startIndex + itemsPerPage, totalItems) : totalItems;

  // Apply maxRows or pagination
  let displayData;
  if (maxRows && !showPagination) {
    displayData = filteredData.slice(0, maxRows);
  } else if (showPagination) {
    displayData = filteredData.slice(startIndex, endIndex);
  } else {
    displayData = filteredData;
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (!showPagination || totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 7;
    
    // Always show first page
    pages.push(1);
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 2; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, current area, and last
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return (
      <Pagination>
        <PaginationInfo>
          Showing {startIndex + 1} - {endIndex} of {totalItems} items
        </PaginationInfo>
        <PaginationControls>
          <PageButton 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M7.5 3L4.5 6L7.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </PageButton>
          
          {pages.map((page, index) => (
            <PageButton
              key={index}
              active={page === currentPage}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={typeof page === 'string'}
            >
              {page}
            </PageButton>
          ))}
          
          <PageButton 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </PageButton>
        </PaginationControls>
      </Pagination>
    );
  };

  return (
    <Container>
      <Heading>
        <TitleText>
          <Title>{title}</Title>
          {description && <Subtitle>{description}</Subtitle>}
        </TitleText>
        {headerActions}
      </Heading>
      
      <SearchFilter>
        <SearchInput
          type="text"
          placeholder={searchPlaceholder}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <FilterButton onClick={handleFilter}>
          Filter
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </FilterButton>
      </SearchFilter>

      <TableWrapper>
        {loading ? (
          <LoadingContainer>
            <Dots />
          </LoadingContainer>
        ) : (
          <Table>
            <TableHeader>
              <TableHeaderRow>
                {columns.map((column) => (
                  <TableHeaderCell key={column.key}>{column.title}</TableHeaderCell>
                ))}
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {displayData.map((record, index) => (
                <TableRowErrorBoundary key={record.id || index}>
                  <TableRow 
                    key={record.id || index}
                    onClick={() => onRowClick && onRowClick(record)}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {column.render 
                          ? column.render(record[column.key], record)
                          : safeRender(record[column.key])
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                </TableRowErrorBoundary>
              ))}
            </TableBody>
          </Table>
        )}
      </TableWrapper>
      
      {/* Mobile Cards - only show if mobileCardConfig is provided */}
      {mobileCardConfig && (
        <MobileCardContainer>
          {displayData.map((record, index) => (
            <MobileCard 
              key={record.id || index} 
              onClick={() => onRowClick && onRowClick(record)}
            >
              <MobileCardHeader>
                <MobileCardTitle>{record[mobileCardConfig.titleKey]}</MobileCardTitle>
                {mobileCardConfig.dateKey && (
                  <MobileCardDate>{record[mobileCardConfig.dateKey]}</MobileCardDate>
                )}
              </MobileCardHeader>
              <MobileCardContent>
                {mobileCardConfig.rows.map((row, rowIndex) => (
                  <MobileCardRow key={rowIndex}>
                    <MobileCardLabel>{row.label}:</MobileCardLabel>
                    <MobileCardValue>{record[row.key]}</MobileCardValue>
                  </MobileCardRow>
                ))}
              </MobileCardContent>
              {/* Actions can be rendered here if needed */}
            </MobileCard>
          ))}
        </MobileCardContainer>
      )}
      
      {renderPagination()}
    </Container>
  );
};

export default FilterableTable;
export { StatusTag, ActionButton, ConfidenceText };
